export interface DemoStep {
  id: string;
  /** Route to navigate to. Use ':project' as placeholder for the user's first project. */
  route: string;
  /** Text typed in the water bar while the audio plays. Must match generate-demo-audio.py. */
  narration: string;
  audioSrc: string;
  /** Total ms to hold this step (should cover audio length + ~500ms buffer). */
  durationMs: number;
  /** data-demo-id value to apply the float/tilt lift effect to. */
  liftTarget?: string;
  /** Tilt angle in degrees: positive tilts right, negative tilts left. */
  liftTilt?: number;
  /** data-demo-id value to move the cursor to (falls back to liftTarget). */
  cursorTarget?: string;
  /** Ms to wait after navigation before starting effects (DOM render delay). */
  renderDelay?: number;
}

export const DEMO_STEPS: DemoStep[] = [
  {
    id: '01-intro',
    route: '/',
    narration: "Welcome to OctoPM — your home for product insight and action across your entire company. Let's take a quick tour.",
    audioSrc: '/audio/onboarding/01-intro.wav',
    durationMs: 8200,
    renderDelay: 400,
  },
  {
    id: '02-whats-on-deck',
    route: '/',
    narration: "Every morning, OctoPM scans your portfolio — Slack threads, Jira tickets, and Linear issues — and surfaces exactly what needs your attention today. No more blindspots.",
    audioSrc: '/audio/onboarding/02-whats-on-deck.wav',
    durationMs: 13400,
    liftTarget: 'whats-on-deck',
    liftTilt: -3,
    cursorTarget: 'whats-on-deck',
    renderDelay: 400,
  },
  {
    id: '03-quick-actions',
    route: '/',
    narration: "Quick Actions are where OctoPM saves you real time. Instead of hunting across Slack, Jira, and Linear to piece together what's happening, OctoPM connects the fragments for you — and surfaces ready-to-send updates in one click.",
    audioSrc: '/audio/onboarding/03-quick-actions.wav',
    durationMs: 16100,
    liftTarget: 'action-suggestions',
    liftTilt: 3,
    cursorTarget: 'action-suggestions',
    renderDelay: 300,
  },
  {
    id: '04-project-grid',
    route: '/',
    narration: "Your portfolio shows live risk scores across every project. Red means attention needed, green means you're clear. OctoPM tracks all your connected tools automatically.",
    audioSrc: '/audio/onboarding/04-project-grid.wav',
    durationMs: 12800,
    liftTarget: 'project-grid',
    liftTilt: -2,
    cursorTarget: 'project-grid',
    renderDelay: 300,
  },
  {
    id: '05-project-detail',
    route: ':project',
    narration: "The project pulse runs our risk algorithm on what's really happening — your team's discussions, how the work is being updated, and patterns across every connected tool. Not just what the tracker says. One honest score.",
    audioSrc: '/audio/onboarding/05-project-detail.wav',
    durationMs: 15700,
    liftTarget: 'project-dashboard-header',
    liftTilt: 2,
    cursorTarget: 'project-dashboard-header',
    renderDelay: 900,
  },
  {
    id: '06-integrations',
    route: '/settings?tab=integrations',
    narration: "Connecting is easy — each integration is a single click. Slack, Jira, Google Workspace, and Linear each unlock richer data for the model. The more you connect, the sharper OctoPM's picture of your work.",
    audioSrc: '/audio/onboarding/06-integrations.wav',
    durationMs: 14800,
    liftTarget: 'integrations-section',
    liftTilt: -3,
    cursorTarget: 'integrations-section',
    renderDelay: 900,
  },
  {
    id: '07-risk-tolerance',
    route: '/settings?tab=risk',
    narration: "You can also tune the model to how your company thinks about risk. Adjust the levers — weighting velocity, communication patterns, blockers, and more — so OctoPM's scores reflect what actually matters to your team.",
    audioSrc: '/audio/onboarding/07-risk-tolerance.wav',
    durationMs: 15000,
    liftTarget: 'risk-section',
    liftTilt: 2,
    cursorTarget: 'risk-section',
    renderDelay: 600,
  },
  {
    id: '08-feedback',
    route: '/',
    narration: "One last thing — we love hearing from you. Hit the feedback button in the sidebar whenever you spot something to improve. We are continuously improving OctoPM, and your feedback shapes every update.",
    audioSrc: '/audio/onboarding/08-feedback.wav',
    durationMs: 13300,
    liftTarget: 'feedback-button',
    liftTilt: 3,
    cursorTarget: 'feedback-button',
    renderDelay: 600,
  },
  {
    id: '09-wrapup',
    route: '/',
    narration: "That's OctoPM. Your dashboard is live and ready. Let's get started.",
    audioSrc: '/audio/onboarding/09-wrapup.wav',
    durationMs: 6300,
    renderDelay: 200,
  },
];
